import { JSX, useContext, useEffect, useState } from "react";
import { ClaimsInsightsSummaryScenario, ClaimsInsightsSummaryScenarioModel } from "@app/projects/network-assessment/pci-dashboard/claims-insights";
import { PciDashboardFiltersContext, PciDashboardFiltersPayload, PciDashboardScenarioTypeEnum, PciDashboardSummaryType } from "@app/projects/network-assessment/pci-dashboard/shared";
import { getPciDashboardScenarioChangeDisplay, getRoundingDisplay, getScenarioDisplay } from "@app/projects/network-assessment/pci-dashboard/shared/utility";
import { DashboardTile, QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles } from "@app/shared/waffles";
import { EllipsisTooltip, LoadingSpinner, formatDateTime } from "@app/shared";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { TextAlignment, WafflesThemes } from "@app/shared/waffles/exports";

interface ClaimsInsightsSummaryScenarioTileProps {
  loading: boolean;
  getSummaries: (
    filtersPayload: PciDashboardFiltersPayload,
    summaryType: PciDashboardSummaryType,
    sortedColumn: string,
    sortDirection: SortDirection,
    isSorting?: boolean,
    isFiltering?: boolean,
  ) => void;
  onSummaryDashboardDetailsViewClick: (
    dashboardDetailsParam: string,
    summaryType: PciDashboardSummaryType,
    scenario?: PciDashboardScenarioTypeEnum,
  ) => void;
  scenarioModel: ClaimsInsightsSummaryScenarioModel;
  summaryType: PciDashboardSummaryType;
}

interface ClaimsInsightsSummaryScenarioTileToppings {
  compareEffectivenessValue: string | JSX.Element;
  compareEfficiencyValue: string | JSX.Element;
  effectivenessValue: string | JSX.Element;
  effectivenessValueChange: JSX.Element;
  efficiencyValue: string | JSX.Element;
  efficiencyValueChange: JSX.Element;
  key: string;
  summaryDescription: string;
  summaryName: JSX.Element;
}

const ClaimsInsightsSummaryScenarioTile = (props: ClaimsInsightsSummaryScenarioTileProps): JSX.Element => {
  const [buildingRecords, setBuildingRecords] = useState<boolean>(false);
  const [loadAllClicked, setLoadAllClicked] = useState<boolean>(false);
  const [ghostProviderScenarios, setGhostProviderScenarios] = useState<ClaimsInsightsSummaryScenarioTileToppings[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [sortedColumn, setSortedColumn] = useState<string>(null);

  const pciDashboardFiltersContext = useContext(PciDashboardFiltersContext);

  useEffect(() => {
    if (!!props.scenarioModel?.summaries) {
      const waffleToppings: ClaimsInsightsSummaryScenarioTileToppings[] = props.scenarioModel?.summaries.map((s, index) => {
        /* eslint-disable sort-keys */
        return {
          key: `${s.summaryName}-${index}`,
          summaryName: (
            <EllipsisTooltip
              key={index}
              text={s.summaryName}
            />
          ),
          summaryDescription: s.summaryDescription,
          effectivenessValue: getEffectivenessAndEfficiencyDisplay(s, "effectivenessValue", index),
          compareEffectivenessValue: getEffectivenessAndEfficiencyDisplay(s, "compareEffectivenessValue", index),
          effectivenessValueChange: getPciDashboardScenarioChangeDisplay(s.effectivenessValueChange, true),
          efficiencyValue: getEffectivenessAndEfficiencyDisplay(s, "efficiencyValue", index),
          compareEfficiencyValue: getEffectivenessAndEfficiencyDisplay(s, "compareEfficiencyValue", index),
          efficiencyValueChange: getPciDashboardScenarioChangeDisplay(s.efficiencyValueChange, true),
        };
        /* eslint-enable sort-keys */
      });

      setGhostProviderScenarios(waffleToppings);
    }
  }, [props.scenarioModel, pciDashboardFiltersContext.displayedFilters]);

  useEffect(() => {
    // when sorting or loading in a large amount of records,
    // there could be some time between when the api call is complete
    // and when the records are visibly displayed
    const resizeObserver = new ResizeObserver((event) => {
      const resizeEntry = event[0]?.target?.children;
      const tbodyElement = [...resizeEntry]?.find(x => x.localName === "tbody");

      if (tbodyElement?.childElementCount === ghostProviderScenarios?.length && buildingRecords) {
        setLoadAllClicked(false);
        setBuildingRecords(false);
      }
    });

    const wafflesTable = document.querySelector(`[data-testid=claims-insights__${props.summaryType}-summary-scenario--table]`);

    if (!!wafflesTable) {
      resizeObserver.observe(wafflesTable);
    }
  }, [ghostProviderScenarios]);

  useEffect(() => {
    if (props.loading && !!ghostProviderScenarios.length) {
      setGhostProviderScenarios([]);
    }
  }, [props.loading]);

  const columnConfig: ColumnConfig = {
    compareEffectivenessValue: {
      alignment: TextAlignment.Center,
      displayElement: <>{getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}<br />Effectiveness</>,
      sizeContentToFit: true,
      testId: "compare-effectiveness-value",
    },
    compareEfficiencyValue: {
      alignment: TextAlignment.Center,
      displayElement: <>{getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}<br />Efficiency</>,
      sizeContentToFit: true,
      testId: "compare-efficiency-value",
    },
    effectivenessValue: {
      alignment: TextAlignment.Center,
      displayElement: <>Latest Timeslice<br />Effectiveness</>,
      sizeContentToFit: true,
      testId: "effectiveness-value",
    },
    effectivenessValueChange: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Change</>,
      sizeContentToFit: true,
      testId: "effectiveness-value-change",
    },
    efficiencyValue: {
      alignment: TextAlignment.Center,
      displayElement: <>Latest Timeslice<br />Efficiency</>,
      sizeContentToFit: true,
      testId: "efficiency-value",
    },
    efficiencyValueChange: {
      alignment: TextAlignment.Center,
      displayElement: <><br />Change</>,
      sizeContentToFit: true,
      testId: "efficiency-value-change",
    },
    summaryDescription: {
      alignment: TextAlignment.Center,
      displayElement: <><br />{props.summaryType === PciDashboardSummaryType.County ? "State" : "Code"}</>,
      sizeContentToFit: true,
      testId: "summary-description",
    },
    summaryName: {
      alignment: TextAlignment.Left,
      displayElement: <><br />{props.summaryType}</>,
      sizeContentToFit: true,
      testId: "summary-name",
    },
  };

  const customSort = (
    records: ClaimsInsightsSummaryScenarioTileToppings[],
    column: keyof ClaimsInsightsSummaryScenarioTileToppings,
    sortedDirection: SortDirections
  ): ClaimsInsightsSummaryScenarioTileToppings[] => {
    let direction: SortDirection = null;

    switch (sortedDirection) {
      case SortDirections.Asc:
        direction = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        direction = SortDirection.Descending;
        break;
      case SortDirections.None:
        direction = SortDirection.None;
        break;
      default:
        direction = SortDirection.Unknown;
        break;
    }

    setSortDirection(direction);
    setSortedColumn(column);
    setBuildingRecords(true);

    props.getSummaries(null, props.summaryType, column, direction, true);

    return records;
  }

  const getEffectivenessAndEfficiencyDisplay = (summary: ClaimsInsightsSummaryScenario, key: keyof ClaimsInsightsSummaryScenario, index: number) => {
    if (!summary[key]) {
      return "---";
    }

    return (
      <QESButton
        onClick={() => props.onSummaryDashboardDetailsViewClick(
          props.summaryType === PciDashboardSummaryType.County ? summary.summaryName : summary.summaryDescription,
          props.summaryType,
          (key === "compareEffectivenessValue" || key == "compareEfficiencyValue") ? pciDashboardFiltersContext.displayedFilters.compareScenario : PciDashboardScenarioTypeEnum.Original
        )}
        qesTestId={`claims-insights__${props.summaryType}-summary-scenario--dashboard-details-${index}`}
        qesType="secondary"
      >
        {getRoundingDisplay(summary[key] as number, false, 2)}
      </QESButton>
    );
  }

  const onLoadAllClick = () => {
    setLoadAllClicked(true);
    setBuildingRecords(true);
    props.getSummaries(null, props.summaryType, sortedColumn, sortDirection);
  }

  return (
    <DashboardTile
      classNames="pci-dashboard__summary-tile dashboard__scrollable"
      loading={props.loading}
      title={`${props.summaryType} Summary - Network Providers`}
      subtitle={`Comparison of ${formatDateTime(pciDashboardFiltersContext.projectRunConfiguration?.dateCreated)} and ${getScenarioDisplay(pciDashboardFiltersContext.displayedFilters.compareScenario)}`}
      widthClass="col-md-12"
    >
      <Waffles<ClaimsInsightsSummaryScenarioTileToppings>
        columns={columnConfig}
        customSort={customSort}
        data={ghostProviderScenarios}
        fullWidth={true}
        passThroughSorting={true}
        testId={`claims-insights__${props.summaryType}-summary-scenario--table`}
        theme={WafflesThemes.Simple}
      />

      {buildingRecords ? (
        <div className="d-flex justify-content-center mt-3">
          <LoadingSpinner />
        </div>
      ) : (
        (props.scenarioModel?.totalCount > ghostProviderScenarios?.length && !loadAllClicked) && (
          <div className="d-flex justify-content-center mt-3">
            <QESButton
              onClick={() => onLoadAllClick()}
              qesTestId={`claims-insights__${props.summaryType}-summary-scenario--load-all`}
              qesType="primary"
            >
              Load All
            </QESButton>
          </div>
        )
      )}
    </DashboardTile>
  )
}

export default ClaimsInsightsSummaryScenarioTile;